import isNil from 'lodash/isNil';
import {Component} from 'panel';

import {useEventListener} from 'common/util/component-helpers';
import {formatDate, parseDate} from 'common/util/date';
import {formatMaxHistoryDays} from 'common/report/util';
import {filterAttributes, parseNumber} from 'common/util';
import {NAVIGATION_KEY_CODES, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {DateRange} from './models/date-range';
import {DateRangeFactory} from './models/date-range-factory';
import {DATE_RANGE_TYPE_DISPLAY_NAMES, TIME_UNIT_DISPLAY_NAMES} from './models/presets';
import {DateRangeType, DisplayContext} from './models/types';
import {getDateLimitedTooltipText, getMinDateStr} from './utils';
import template from './index.jade';
import css from './index.styl?inline';
import './calendar-input';

/**
 * TODO: @jlack -- write tests for component before adding to mixpanel-common
 */

export class DateRangePicker extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'date-range': {type: `json`, description: `interface: DateRange`},
      'display-context': {type: `string`},
      'hide-hours': {type: `boolean`},
      'max-date': {type: `string`},
      'show-max-date-past-context': {
        type: `boolean`,
        description: `shows context for projects with max date in the past`,
      },
      'max-history-days': {type: `string`},
      'show-upsell': {type: `string`},
    };
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);
    switch (attr) {
      case `date-range`:
      case `display-context`: {
        this.updateDateRange();
        requestAnimationFrame(() => this.dispatchEvent(new CustomEvent(`draw`)));
        break;
      }
    }
  }

  updateDateRange() {
    const dateRangeAttrs = this.attr(`date-range`) || {};
    const displayContext = this.getDisplayContext();
    this.update({
      dateRange: DateRangeFactory.createDateRange(dateRangeAttrs, {displayContext}),
    });
  }

  connectedCallback() {
    this.updateDateRange();
    super.connectedCallback();
    useEventListener(this, window, `keydown`, (ev) => {
      if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER) {
        this.dispatchEvent(new CustomEvent(`confirmDateRange`));
      }
    });
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
      defaultState: {
        dateRange: null,
      },
      helpers: {
        ...super.config.helpers,
        filterAttributes,
        DateRangeType,
        DATE_RANGE_TYPE_DISPLAY_NAMES,
        TIME_UNIT_DISPLAY_NAMES,
        propagateEvent: (ev) => {
          propagateEvent(this, ev);
        },
        isValidDateRange: () => {
          return this.state.dateRange.isValid();
        },
        isWithinMaxHistoryLimit: () => {
          const maxHistoryDays = this.helpers.maxHistoryDays();
          if (Number.isInteger(maxHistoryDays)) {
            return this.state.dateRange.isWithinMaxHistoryLimit(maxHistoryDays);
          } else {
            return true;
          }
        },
        changedDateRangeType: (ev) => {
          const {dateRange} = this.state;
          const type = dateRange.getDateRangeTypeOptions().find((type) => {
            return DATE_RANGE_TYPE_DISPLAY_NAMES[type] === ev.detail.value;
          });
          const updates = {type};
          // When switching from "Since" to "On" without changing the selected date,
          // we need to make sure "to" is updated from null
          if (type === DateRangeType.On || type === DateRangeType.StartsOn || type === DateRangeType.EndsOn) {
            updates.to = dateRange.from;
          }
          // changing from a rolling "between" or "on", set "from" from relative to absolute
          if (type === DateRangeType.Since && dateRange.rolling && DateRange.isRelativeDate(dateRange.from)) {
            updates.from = DateRange.convertRelativeDateToCalendarDate(dateRange.from);
          }
          this.dispatchChangeDateRangeEvent(updates);
        },
        changedDates: (ev) => {
          const {rolling, type} = this.state.dateRange;
          let {from, to} = ev.detail;
          if (type === DateRangeType.On || type === DateRangeType.StartsOn || type === DateRangeType.EndsOn) {
            to = from;
          } else if (type === DateRangeType.Since) {
            to = null;
          }
          if (rolling) {
            [from, to] = [
              DateRange.convertCalendarDateToRelativeDate(from),
              DateRange.convertCalendarDateToRelativeDate(to),
            ];
          }
          this.dispatchChangeDateRangeEvent({from, to});
        },
        mouseenterRangeInput: () => {
          this.update({isRangeInputMousedOver: true});
        },
        mouseleaveRangeInput: () => {
          this.update({isRangeInputMousedOver: false});
        },
        blurredRangeInput: () => {
          this.update({isRangeInputFocused: false});
        },
        focusedRangeInput: () => {
          this.update({isRangeInputFocused: true});
        },
        changedRelativeRangeValue: (ev) => {
          const value = parseNumber(ev.target.value);
          this.dispatchChangeDateRangeEvent({window: {value}});
        },
        changedRelativeRangeUnit: (ev) => {
          const {dateRange} = this.state;
          const unit = dateRange.getTimeUnitOptions().find((unit) => TIME_UNIT_DISPLAY_NAMES[unit] === ev.detail.value);
          this.dispatchChangeDateRangeEvent({window: {unit}});
          this.selectRelativeInput();
        },
        handleInsertCalendarInput: (vnode) => {
          switch (this.state.dateRange.type) {
            case DateRangeType.On:
            case DateRangeType.Since:
              requestAnimationFrame(() => vnode.elm.focusFromInput());
              break;
            default:
              break;
          }
        },
        minDate: () => {
          return getMinDateStr({maxHistoryDays: this.helpers.maxHistoryDays()});
        },
        maxHistoryDays: () => {
          return this.getNumberAttribute(`max-history-days`);
        },
        maxHistoryDaysText: () => {
          return formatMaxHistoryDays(this.helpers.maxHistoryDays());
        },
        dateLimitedTooltipText: () => {
          return getDateLimitedTooltipText({
            maxHistoryDays: this.helpers.maxHistoryDays(),
            showUpsell: this.helpers.showUpsell(),
            beforeEpoch: isNil(this.helpers.minDate()),
          });
        },
        showMaxDateContext: () => {
          return (
            !!this.attr(`show-max-date-past-context`) &&
            !!this.attr(`max-date`) &&
            [DateRangeType.RelativeAfter, DateRangeType.Since].includes(this.state.dateRange.type)
          );
        },
        overrideMaxDateContextZIndex: () => {
          // Override z index for since to make it show above the calendar.
          return [DateRangeType.Since].includes(this.state.dateRange.type);
        },
        maxDateContextText: () => {
          return `Ending ${this.getMaxDateLabel()}`;
        },
        maxDateTooltipText: () => {
          return `Project contains data through ${this.getMaxDateLabel()}`;
        },
        isRangeInputActive: () => {
          return this.state.isRangeInputFocused || this.state.isRangeInputMousedOver;
        },
        showUpsell: () => {
          return this.isAttributeEnabled(`show-upsell`) && Number.isInteger(this.helpers.maxHistoryDays());
        },
        clickedRollingCheckbox: () => {
          const {dateRange} = this.state;
          let {from, to} = dateRange;
          const rolling = !dateRange.rolling;
          if (rolling) {
            [from, to] = [
              from && DateRange.convertCalendarDateToRelativeDate(from),
              to && DateRange.convertCalendarDateToRelativeDate(to),
            ];
          } else {
            [from, to] = [
              from && DateRange.convertRelativeDateToCalendarDate(from),
              to && DateRange.convertRelativeDateToCalendarDate(to),
            ];
          }
          this.dispatchChangeDateRangeEvent({rolling, from, to});
        },
        canUseRolling: () => {
          return this.state.dateRange.canUseRolling();
        },
        getRollingCheckboxText: () => {
          switch (this.state.dateRange.type) {
            case DateRangeType.Between:
              return `Rolling range`;
            case DateRangeType.EndsOn:
            case DateRangeType.StartsOn:
            case DateRangeType.On:
              return `Rolling date`;
          }
        },
        getRollingInfoText: () => {
          switch (this.state.dateRange.type) {
            case DateRangeType.Between:
              return `Dates adjust relative to current day`;
            case DateRangeType.EndsOn:
            case DateRangeType.StartsOn:
            case DateRangeType.On:
              return `Date adjusts relative to current day`;
          }
        },
      },
    };
  }

  focusRelativeInput() {
    this.relativeRangeInput.focus();
  }

  selectRelativeInput() {
    this.relativeRangeInput.select();
  }

  focusFromInput() {
    if (this.initialized) {
      this.calendarInput.focusFromInput();
    }
  }

  selectFromInput() {
    if (this.initialized) {
      this.calendarInput.selectFromInput();
    }
  }

  focusToInput() {
    if (this.initialized) {
      this.calendarInput.focusToInput();
    }
  }

  selectToInput() {
    if (this.initialized) {
      this.calendarInput.selectToInput();
    }
  }

  dispatchChangeDateRangeEvent(attributes) {
    const dateRange = this.state.dateRange.mergeAttributes(attributes);
    this.dispatchEvent(new CustomEvent(`changeDateRange`, {detail: {dateRange}}));
  }

  get calendarInput() {
    return this.el.querySelector(`mp-calendar-input`);
  }

  get relativeRangeInput() {
    return this.el.querySelector(`input.relative-range-value`);
  }

  getDisplayContext() {
    return this.getAttribute(`display-context`) || DisplayContext.Default;
  }

  getMaxDateLabel() {
    return formatDate(parseDate(this.attr(`max-date`)));
  }
}

export const DateRangePickerEl = defineAndMakeJsxFactory(`mp-date-range-picker`, DateRangePicker);
